import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  ONBOARDING_AI_TOOL_OPTIONS,
  ONBOARDING_EXPERIENCE_OPTIONS,
  ONBOARDING_GOAL_OPTIONS,
  ONBOARDING_USAGE_PATTERN_OPTIONS,
  ONBOARDING_WEAK_AREA_OPTIONS,
  datadogLlmUserUrl,
  type AdminUserSummary,
} from '@prepzio/shared';
import { IconAlertTriangle, IconArrowLeft } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { InfoTip } from '../components/InfoTip';
import {
  QuestionDetailModal,
  SessionQuestions,
  statusColor,
} from '../components/QuestionInspector';
import { useAppConfig, useResetUserData, useUserDetail, useUsers } from '../hooks/use-content';
import { useI18n, usePlural, useT } from '../i18n';
import { errorMessage } from '../lib/ui';

/** Short absolute date-time, in the panel's language rather than the browser's. */
function fmtDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/** Map an onboarding enum value to its human label (falls back to the raw value). */
function labelOf(
  options: { value: string; label: string }[],
  value: string | null | undefined,
): string {
  if (!value) return '—';
  return options.find((o) => o.value === value)?.label ?? value;
}

export function UsersScreen() {
  const { userId } = useParams();
  return userId ? <UserDetail id={userId} /> : <UserList />;
}

function UserList() {
  const { t, locale } = useI18n();
  const usersQuery = useUsers();
  const navigate = useNavigate();
  const users = usersQuery.data?.items ?? [];

  if (usersQuery.isError)
    return (
      <Text c="red" size="sm">
        {errorMessage(usersQuery.error)}
      </Text>
    );

  return (
    <Stack gap="md">
      <Group gap={6} align="center">
        <Text c="dimmed" size="sm">
          {t('users.intro')}
        </Text>
        <InfoTip label={t('users.infoTip')} />
      </Group>
      <Card withBorder padding={0} radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('users.colUser')}</Table.Th>
                <Table.Th>{t('users.colPlan')}</Table.Th>
                <Table.Th>{t('users.colPersona')}</Table.Th>
                <Table.Th>{t('users.colRoadmaps')}</Table.Th>
                <Table.Th>{t('users.colJoined')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((u: AdminUserSummary) => (
                <Table.Tr
                  key={u.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/users/${u.id}`)}
                >
                  <Table.Td>
                    <Text size="sm" fw={600} style={{ wordBreak: 'break-all' }}>
                      {u.email ?? u.id}
                    </Text>
                    {u.name && (
                      <Text c="dimmed" size="xs">
                        {u.name}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={u.entitlementActive ? 'teal' : 'gray'} tt="none">
                      {u.planId}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {u.experienceLevel ? (
                      <Group gap={4} wrap="wrap">
                        <Badge variant="light" color="gray" tt="none">
                          {labelOf(ONBOARDING_EXPERIENCE_OPTIONS, u.experienceLevel)}
                        </Badge>
                        <Badge variant="light" color="gray" tt="none">
                          {labelOf(ONBOARDING_GOAL_OPTIONS, u.goal)}
                        </Badge>
                      </Group>
                    ) : (
                      <Text c="dimmed" size="xs">
                        {t('users.noOnboarding')}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{u.roadmapCount}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {fmtDate(u.createdAt, locale)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {users.length === 0 && !usersQuery.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">
                      {t('users.none')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}

function UserDetail({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const plural = usePlural();
  const detailQuery = useUserDetail(id);
  const detail = detailQuery.data ?? null;

  return (
    <Stack gap="md">
      <QuestionDetailModal />
      <Button
        component={Link}
        to="/users"
        variant="subtle"
        size="xs"
        leftSection={<IconArrowLeft size={14} />}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('users.allUsers')}
      </Button>

      {detailQuery.isLoading && (
        <Text c="dimmed" size="sm">
          {t('common.loading')}
        </Text>
      )}
      {detailQuery.isError && (
        <Text c="red" size="sm">
          {errorMessage(detailQuery.error)}
        </Text>
      )}

      {detail && (
        <>
          {/* Identity */}
          <Card withBorder padding="lg" radius="md">
            <Title order={4} style={{ wordBreak: 'break-all' }}>
              {detail.user.email ?? detail.user.id}
            </Title>
            <Group gap="xs" mt={4}>
              {detail.user.name && (
                <Text c="dimmed" size="sm">
                  {detail.user.name}
                </Text>
              )}
              {detail.user.emailVerified && (
                <Badge variant="light" color="green">
                  {t('users.verified')}
                </Badge>
              )}
              <Badge
                variant="light"
                color={detail.entitlement.isActive ? 'teal' : 'gray'}
                tt="none"
              >
                {detail.entitlement.planId}
              </Badge>
              <Text c="dimmed" size="sm">
                {t('users.joined', { date: fmtDate(detail.user.createdAt, locale) })}
              </Text>
              <Anchor
                href={datadogLlmUserUrl(detail.user.id)}
                target="_blank"
                rel="noreferrer"
                size="sm"
              >
                {t('users.llmTraces')}
              </Anchor>
            </Group>
          </Card>

          {/* Subscription / entitlement (PRE-247) */}
          <Card withBorder padding="lg" radius="md">
            <Text fw={600} mb="sm">
              {t('users.subscription')}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <Field label={t('users.plan')}>{detail.entitlement.planId}</Field>
              <Field label={t('common.status')}>
                {t(detail.entitlement.isActive ? 'users.active' : 'users.inactiveFree')}
              </Field>
              <Field label={t('users.product')}>
                {detail.entitlement.productIdentifier ?? '—'}
              </Field>
              <Field label={t('users.store')}>{detail.entitlement.store ?? '—'}</Field>
              <Field label={t('users.expires')}>
                {fmtDate(detail.entitlement.expiresAt, locale)}
              </Field>
              <Field label={t('users.willRenew')}>
                {t(detail.entitlement.willRenew ? 'common.yes' : 'common.no')}
              </Field>
            </SimpleGrid>
          </Card>

          {/* Onboarding profile */}
          <Card withBorder padding="lg" radius="md">
            <Text fw={600} mb="sm">
              {t('users.onboardingProfile')}
            </Text>
            {detail.profile ? (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <Field label={t('users.experience')}>
                  {labelOf(ONBOARDING_EXPERIENCE_OPTIONS, detail.profile.experienceLevel)}
                </Field>
                <Field label={t('users.goal')}>
                  {labelOf(ONBOARDING_GOAL_OPTIONS, detail.profile.goal)}
                </Field>
                <Field label={t('users.aiHabit')}>
                  <Group gap={4} wrap="wrap">
                    {detail.profile.aiUsagePattern.length === 0 && <Text size="sm">—</Text>}
                    {detail.profile.aiUsagePattern.map((p) => (
                      <Badge key={p} variant="light" color="gray" tt="none">
                        {labelOf(ONBOARDING_USAGE_PATTERN_OPTIONS, p)}
                      </Badge>
                    ))}
                  </Group>
                </Field>
                <Field label={t('users.completed')}>
                  {fmtDate(detail.profile.onboardingCompletedAt, locale)}
                </Field>
                <Field label={t('users.weakAreasSelf')}>
                  <Group gap={4} wrap="wrap">
                    {detail.profile.weakAreas.length === 0 && <Text size="sm">—</Text>}
                    {detail.profile.weakAreas.map((w) => (
                      <Badge key={w} variant="light" color="gray" tt="none">
                        {labelOf(ONBOARDING_WEAK_AREA_OPTIONS, w)}
                      </Badge>
                    ))}
                  </Group>
                </Field>
                <Field label={t('users.aiTools')}>
                  <Group gap={4} wrap="wrap">
                    {detail.profile.aiTools.length === 0 && <Text size="sm">—</Text>}
                    {detail.profile.aiTools.map((tool) => (
                      <Badge key={tool} variant="light" color="gray" tt="none">
                        {labelOf(ONBOARDING_AI_TOOL_OPTIONS, tool)}
                      </Badge>
                    ))}
                  </Group>
                </Field>
              </SimpleGrid>
            ) : (
              <Text c="dimmed" size="sm">
                {t('users.noOnboardingYet')}
              </Text>
            )}
          </Card>

          {/* Measured weak skills (PRE-194) — from graded answers, not self-report */}
          <Card withBorder padding="lg" radius="md">
            <Group gap={6} align="center" mb="sm">
              <Text fw={600}>{t('users.measuredWeak')}</Text>
              <InfoTip
                label={t('users.measuredWeakTip', { threshold: detail.weakSkillThreshold })}
              />
            </Group>
            {detail.gradedReportCount === 0 ? (
              <Text c="dimmed" size="sm">
                {t('users.noGraded')}
              </Text>
            ) : detail.measuredWeakSkills.length === 0 ? (
              <Text c="dimmed" size="sm">
                {plural(
                  'users.noneBelowThreshold_one',
                  'users.noneBelowThreshold',
                  detail.gradedReportCount,
                  { threshold: detail.weakSkillThreshold },
                )}
              </Text>
            ) : (
              <>
                <Text c="dimmed" size="xs" mb="xs">
                  {plural(
                    'users.weakestFirst_one',
                    'users.weakestFirst',
                    detail.gradedReportCount,
                    { threshold: detail.weakSkillThreshold },
                  )}
                </Text>
                <Group gap="xs" wrap="wrap">
                  {detail.measuredWeakSkills.map((skill) => (
                    <Badge
                      key={skill.skillId}
                      variant="light"
                      color={skill.mastery < 40 ? 'red' : 'orange'}
                      tt="none"
                      leftSection={<IconAlertTriangle size={12} />}
                    >
                      {t('users.weakSkill', {
                        title: skill.title,
                        mastery: skill.mastery,
                        count: skill.count,
                      })}
                    </Badge>
                  ))}
                </Group>
              </>
            )}
          </Card>

          {/* Sessions with Q&A */}
          <Card withBorder padding="lg" radius="md">
            <Text fw={600} mb="sm">
              {t('users.sessions', { count: detail.sessions.length })}
            </Text>
            {detail.sessions.length === 0 && (
              <Text c="dimmed" size="sm">
                {t('users.noSessions')}
              </Text>
            )}
            <Stack gap="md">
              {detail.sessions.map((s) => (
                <Card key={s.id} withBorder radius="sm" padding="sm" bg="var(--mantine-color-body)">
                  <Group gap="xs" wrap="wrap" mb="xs">
                    <Badge variant="light" color="gray">
                      {s.sessionType.replace(/_/g, ' ')}
                    </Badge>
                    <Badge variant="light" color={statusColor(s.status)}>
                      {s.status}
                    </Badge>
                    <Text c="dimmed" size="xs">
                      {plural('users.sessionMeta_one', 'users.sessionMeta', s.questions.length, {
                        date: fmtDate(s.createdAt, locale),
                      })}
                    </Text>
                  </Group>
                  <SessionQuestions questions={s.questions} />
                </Card>
              ))}
            </Stack>
          </Card>

          {/* Roadmaps */}
          <Card withBorder padding="lg" radius="md">
            <Text fw={600} mb="sm">
              {t('users.roadmaps', { count: detail.roadmaps.length })}
            </Text>
            {detail.roadmaps.length === 0 && (
              <Text c="dimmed" size="sm">
                {t('users.noRoadmaps')}
              </Text>
            )}
            <Stack gap="xs">
              {detail.roadmaps.map((r) => (
                <Group key={r.id} justify="space-between" wrap="nowrap">
                  <div style={{ minWidth: 0 }}>
                    <Anchor component={Link} to={`/roadmaps/${r.id}`} size="sm">
                      {r.isSimulation ? '🧪 ' : ''}
                      {r.title}
                    </Anchor>
                    <Text c="dimmed" size="xs">
                      {r.status} · {fmtDate(r.createdAt, locale)}
                    </Text>
                  </div>
                  <Badge variant="light" color={statusColor(r.status)}>
                    {r.status}
                  </Badge>
                </Group>
              ))}
            </Stack>
          </Card>

          {/* Danger zone — BR-85 reset */}
          <ResetDataZone id={id} email={detail.user.email} />
        </>
      )}
    </Stack>
  );
}

/**
 * BR-85 danger zone: erase all of a user's learner data (onboarding, sessions,
 * roadmaps, mastery, …) while keeping their sign-in. The user can still log in
 * but starts over as a fresh signup. Guarded by typing the exact email.
 */
function ResetDataZone({ id, email }: { id: string; email: string | null }) {
  const t = useT();
  const [opened, { open, close }] = useDisclosure(false);
  const [confirm, setConfirm] = useState('');
  const reset = useResetUserData(id);
  /**
   * PRE-129: the allowlist, read before the click instead of after it.
   *
   * `admin.resetAllowedEmails` defaults to empty — a deliberate safe default
   * (BR-85) — so out of the box every reset is a 403. The panel used to surface
   * that as an error on the confirm-email input, which reads as "you typed the
   * address wrong" and sends you back to retype an address that was correct.
   * Nothing said the word "allowlist" or where to find it.
   */
  const config = useAppConfig();
  const allowlist = (config.data?.items.find((i) => i.key === 'admin.resetAllowedEmails')
    ?.effective ?? []) as string[];
  const allowlisted =
    !!email && allowlist.some((e) => e.trim().toLowerCase() === email.toLowerCase());
  const canReset = !!email && allowlisted && confirm === email && !reset.isPending;

  function onConfirm() {
    if (!email) return;
    reset.mutate(email, {
      onSuccess: () => {
        setConfirm('');
        close();
      },
    });
  }

  return (
    <Card withBorder padding="lg" radius="md" style={{ borderColor: 'var(--mantine-color-red-4)' }}>
      <Group gap={6} mb={4}>
        <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />
        <Text fw={600} c="red">
          {t('users.dangerZone')}
        </Text>
        <InfoTip label={t('users.dangerTip')} />
      </Group>
      <Text c="dimmed" size="sm" mb="sm">
        {t('users.dangerHint')}
      </Text>

      {/* PRE-129: say WHY it is blocked, and where to unblock it. */}
      {!config.isPending && !allowlisted && !!email && (
        <Alert color="yellow" variant="light" mb="sm">
          <Text size="sm">{t('users.notAllowlisted', { email })}</Text>
          <Text size="sm" c="dimmed" mt={4}>
            <Link to="/config?key=admin.resetAllowedEmails">{t('users.addToAllowlist')}</Link>
          </Text>
        </Alert>
      )}

      <Button
        color="red"
        variant="light"
        size="xs"
        onClick={open}
        disabled={!email || !allowlisted}
      >
        {t('users.resetButton')}
      </Button>

      <Modal opened={opened} onClose={close} title={t('users.resetTitle')} centered>
        <Stack gap="sm">
          <Text size="sm">{t('users.resetBody', { email: email ?? '' })}</Text>
          <Text size="sm" c="dimmed">
            {t('users.typeEmail')}
          </Text>
          <TextInput
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            placeholder={email ?? ''}
            autoFocus
          />
          {/* PRE-129: a server refusal is not a typo in this field. */}
          {reset.isError && (
            <Alert color="red" variant="light">
              {errorMessage(reset.error)}
            </Alert>
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" size="xs" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              color="red"
              size="xs"
              disabled={!canReset}
              loading={reset.isPending}
              onClick={onConfirm}
            >
              {t('users.resetConfirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
        {label}
      </Text>
      {typeof children === 'string' ? <Text size="sm">{children}</Text> : children}
    </div>
  );
}
